import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import { connect } from 'react-redux';

import { VpMediaTagInput } from '@vp-ui';
import VpConditionalUpgradeWrapper from '@vp-ui/components/VpConditionalUpgradeWrapper/VpConditionalUpgradeWrapper';
import { getContentTaggingEnabled, getMediaThumbnailEditingEnabled } from '@src/redux/modules/auth';
import { VpVideoPlayer, VpIcon, VpDocViewer, VpButton } from '@vp-ui/components';
import { useMediaTableContext } from '@src/hooks';
import { copyToClipboard } from '@src/utils/copyToClipboard';
import { useVpSnackbar } from '@vp-ui/hooks/index';
import { useFetchMediaById } from '@src/hooks/react-query/useMediaData';
import { buildCustomVideoPeelUrl } from '@src/helpers/customDomainsHelper';
import VpMediaDownloadModal from '@vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaDownloadModal';
import config from '@src/config';

const VideoPlayerContainer = styled(Box)({
  justifyContent: 'center',
  alignContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '6px',
  // @TODO Check if changing this breaks
  height: 'auto',
  width: '100%',
});

const StyledIcon = styled(VpIcon)({
  position: 'relative',
  width: '100%',
  height: '100%',
});
const StyledImage = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});
const StyledSkeleton = styled(Skeleton)({
  width: '100%',
  height: '100%',
  position: 'absolute',
});

export const buildShareLink = (customDomain, currentOrigin, rawPath) => {
  const baseUrl = customDomain ? buildCustomVideoPeelUrl(customDomain) : currentOrigin;

  const linkToMediaPage = `${baseUrl}/share/${rawPath}`;

  return linkToMediaPage;
};

const VpMediaInfoPreview = ({
  media,
  contentTaggingEnabled,
  customizeThumbnailEnabled,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

  const isVideo = media?.type === 'VIDEO';
  const isImage = media?.type === 'IMAGE';
  const isDocument = !isVideo && !isImage;
  const thumbnailUrl = media?.thumbnailUrl || media?.path;

  const { setUpdatingThumbnail } = useMediaTableContext();
  const { addSnackbarInfo, addSnackbarError } = useVpSnackbar();
  const { data, isLoading: mediaLoading } = useFetchMediaById(media?.mediaId);

  const handleCustomizeThumbnail = () => {
    if (customizeThumbnailEnabled) {
      return setUpdatingThumbnail(media?.mediaId);
    }

    addSnackbarError({
      message:
        'Your current plan does not allow customizing video thumbnails. Please upgrade on the Billing page.',
      title: 'Error!',
    });
  };

  const handleOnLoad = () => {
    setIsLoading(false);
  };
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const buttons = [
    {
      label: 'Thumbnail',
      icon: 'Image',
      action: handleCustomizeThumbnail,
      visible: !!isVideo,
    },
    {
      label: 'Download',
      icon: 'Download',
      action: () => {
        setIsDownloadModalOpen(true);
      },
      visible: true,
    },
    {
      label: 'Share',
      icon: 'Share',
      action: () => {
        // white label versions still need to display the share video urls
        // on the regular app.videopeel.com url, until we implement
        // something specific to handle that
        const shareLink = buildShareLink(
          data?.data?.campaignCustomDomain,
          config.shareVideosLinkUrl,
          media?.mediaRawPath
        );
        copyToClipboard(shareLink);
        addSnackbarInfo({ message: 'Copied link to clipboard', title: '' });
      },
      visible: !!isVideo,
    },
  ];

  return (
    <Stack sx={{ alignItems: 'center' }}>
      {isDownloadModalOpen && (
        <VpMediaDownloadModal
          _onClose={() => setIsDownloadModalOpen(false)}
          isVisible={isDownloadModalOpen}
          fileId={media?.mediaId || media?.attachmentId}
          fileType={media?.type}
          fileData={data?.data || media}
        />
      )}
      <Box sx={{ width: '100%' }}>
        <VpConditionalUpgradeWrapper condition={!contentTaggingEnabled} disableFlex>
          {({ isDisabled }) => (
            <VpMediaTagInput
              value={media?.tags}
              mediaId={media?.mediaId || media?.attachmentId}
              isVideo={!!media?.mediaId}
              disabled={isDisabled}
            />
          )}
        </VpConditionalUpgradeWrapper>
      </Box>
      <div style={{ display: 'flex', width: '100%' }}>
        {buttons.map(
          (b) =>
            b.visible && (
              <VpButton
                key={b.label}
                onClick={b.action}
                variant="outlined"
                color="neutral"
                sx={{ gap: '10px', flexGrow: 1, flexBasis: '33%' }}
                disabled={mediaLoading}
              >
                <VpIcon icon={b.icon} sx={{ width: '16px' }} /> {b.label}
              </VpButton>
            )
        )}
      </div>
      <VideoPlayerContainer>
        {isDocument && <VpDocViewer document={media} />}
        {isVideo ? (
          <VpVideoPlayer
            onLoad={handleOnLoad}
            mediaUrl={media?.mediaUrl}
            thumbnailUrl={thumbnailUrl}
            noDownload
          />
        ) : hasError ? (
          <StyledIcon icon="Paperclip" />
        ) : (
          <StyledImage
            key={thumbnailUrl}
            component="img"
            onLoad={handleOnLoad}
            onError={handleError}
            src={thumbnailUrl}
          />
        )}
        {isLoading && <StyledSkeleton variant="rectangular" />}
      </VideoPlayerContainer>
    </Stack>
  );
};

VpMediaInfoPreview.propTypes = {
  media: PropTypes.shape({
    mediaUrl: PropTypes.string,
    mediaId: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    path: PropTypes.string,
    attachmentId: PropTypes.string,
    type: PropTypes.string,
    tags: PropTypes.array,
    mediaRawPath: PropTypes.string,
    previewUrl: PropTypes.string,
    originalFileName: PropTypes.string,
  }),
  contentTaggingEnabled: PropTypes.bool,
  customizeThumbnailEnabled: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  contentTaggingEnabled: getContentTaggingEnabled(state),
  customizeThumbnailEnabled: getMediaThumbnailEditingEnabled(state),
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(VpMediaInfoPreview);



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoPreview.jsx